/**
 * Sales Hub BPF stage identity, and the read-time normalizer for the stored
 * `sales.stages` config.
 *
 * The 2026-09-29 Sales Hub cutover (docs/design/sales-hub-cutover-2026-10.md)
 * renamed stages IN PLACE, keeping their ids, and split the shared Stop Loss
 * Review stage into four funding-type stages. `opportunity.stepname` is text
 * written on stage change and is NOT rewritten by a rename, so open
 * opportunities carry the old and the new names side by side until they move.
 *
 * The stored config rows (prod and dev) predate all of this and carry no
 * bpfStageId, so every reader runs the stored stages through
 * `normalizeSalesStages` instead of trusting them as saved:
 *   1. fill a missing bpfStageId from the known id table, by name
 *   2. rename a renamed stage to its new name, and keep the old name as a
 *      `mappedTo` alias so opportunities still carrying it are matched
 *   3. add the Stop Loss Review bucket before the first case stage, with the
 *      four funding-type stages (and the pre-rename "Not Self Funded") mapped
 *      into it
 * It is idempotent, so a config an admin re-saves in the new shape passes
 * through unchanged.
 */

import type { SalesStageConfig } from './types';

/** BPF stage ids (dts_veracitysalesprocess). Stable across the cutover. */
export const BPF_STAGE_IDS = {
  newOpportunity: '5a47c4aa-5647-4bfd-afd2-6f0bcb12a794',
  /** Removed at cutover; history rows still point at it. */
  qualify: 'd3ded8f3-2e7b-475a-8c9b-be96bacb8cf9',
  /** Removed at cutover; history rows still point at it. */
  solutionScoping: 'c251728c-ff71-4919-9490-a4999bc3c206',
  proposalCreation: 'c248197f-0cf7-4851-85d3-a605a165bf46',
  presentation: '2d945b11-4433-4e44-bda9-633b7ec41ba7',
  /** The shared Stop Loss Review stage, deleted at cutover; history only. */
  stopLossShared: '2d328a8a-0b1f-4e1d-81d4-71c48490c1b1',
  stopLossSelfFunded: '3abfdbfe-f702-4112-9431-980091346a4e',
  stopLossLevelFunded: '19c6e454-edb8-4ba2-b1ee-464404364522',
  stopLossFullyInsured: '6f4017d7-dd5a-4732-b143-bddaaa73a280',
  stopLossVirginGroup: 'cb44d2eb-7171-4985-995d-65d1ff8c39bc',
} as const;

export const STOP_LOSS_BUCKET = 'Stop Loss Review';

/** Old name -> new name, for stages renamed in place. */
export const STAGE_RENAMES: ReadonlyArray<readonly [string, string]> = [
  ['Working', 'New Opportunity One Card'],
  ['Proposal Creation For Open Case', 'Proposal Creation'],
];

/** Funding-type stop loss stages, all reported as one bucket. */
const STOP_LOSS_STAGES: ReadonlyArray<readonly [string, string | undefined]> = [
  ['Stop Loss Review Self Funded', BPF_STAGE_IDS.stopLossSelfFunded],
  ['Stop Loss Review Level Funded', BPF_STAGE_IDS.stopLossLevelFunded],
  ['Stop Loss Review Fully Insured', BPF_STAGE_IDS.stopLossFullyInsured],
  ['Stop Loss Review Virgin Group', BPF_STAGE_IDS.stopLossVirginGroup],
  // Pre-rename name of the Level Funded stage; its id now belongs to the entry above.
  ['Not Self Funded', undefined],
];

/** Every name a stage id has been known by, old and new. */
const ID_BY_NAME: Record<string, string> = {
  'New Opportunity One Card': BPF_STAGE_IDS.newOpportunity,
  Working: BPF_STAGE_IDS.newOpportunity,
  Qualify: BPF_STAGE_IDS.qualify,
  'Solution Scoping': BPF_STAGE_IDS.solutionScoping,
  'Proposal Creation': BPF_STAGE_IDS.proposalCreation,
  'Proposal Creation For Open Case': BPF_STAGE_IDS.proposalCreation,
  Presentation: BPF_STAGE_IDS.presentation,
  [STOP_LOSS_BUCKET]: BPF_STAGE_IDS.stopLossShared,
};

/** Current display name for a stepname, folding renamed stages onto their new name. */
export function currentStageName(stepName: string): string {
  for (const [from, to] of STAGE_RENAMES) if (stepName === from) return to;
  return stepName;
}

export function normalizeSalesStages(input: readonly SalesStageConfig[]): SalesStageConfig[] {
  const byOrder = [...input].sort((a, b) => a.order - b.order);

  // 1. ids by name, real stages only (a mappedTo alias rides its target's id)
  let stages = byOrder.map((s) => {
    if (s.bpfStageId || s.mappedTo) return { ...s };
    const id = ID_BY_NAME[s.name];
    return id ? { ...s, bpfStageId: id } : { ...s };
  });

  const has = (name: string) => stages.some((s) => s.name === name);
  const aliases: SalesStageConfig[] = [];

  // 2. renames: the real stage takes the new name, the old name becomes an alias
  for (const [from, to] of STAGE_RENAMES) {
    const old = stages.find((s) => s.name === from && !s.mappedTo);
    if (old && !has(to)) old.name = to;
    if (!has(from) && has(to)) {
      aliases.push({ name: from, isCase: false, isPipeline: false, color: null, order: 0, mappedTo: to });
    }
  }
  // Existing aliases that pointed at an old name follow the rename.
  for (const s of stages) {
    if (s.mappedTo) s.mappedTo = currentStageName(s.mappedTo);
  }

  // 3. stop loss bucket, placed before the first case stage
  if (!has(STOP_LOSS_BUCKET)) {
    const early = stages.find((s) => s.name === 'New Opportunity One Card');
    const bucket: SalesStageConfig = {
      name: STOP_LOSS_BUCKET,
      isCase: false,
      isPipeline: early?.isPipeline ?? true,
      color: null,
      order: 0,
      bpfStageId: BPF_STAGE_IDS.stopLossShared,
    };
    const firstCase = stages.findIndex((s) => s.isCase && !s.mappedTo);
    stages = firstCase < 0
      ? [...stages, bucket]
      : [...stages.slice(0, firstCase), bucket, ...stages.slice(firstCase)];
  }
  for (const [name, id] of STOP_LOSS_STAGES) {
    if (has(name)) continue;
    aliases.push({
      name,
      isCase: false,
      isPipeline: false,
      color: null,
      order: 0,
      mappedTo: STOP_LOSS_BUCKET,
      ...(id ? { bpfStageId: id } : {}),
    });
  }

  return [...stages, ...aliases].map((s, i) => ({ ...s, order: i }));
}
